import { useState } from 'react';
import { AlertTriangle, Check, Pencil, Tags, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { TagPill, TAG_COLORS, tagTint } from '@/components/ui/TagPill';
import { toast } from '@/components/ui/Toast';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { HelpHint } from '@/features/help/HelpHint';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import {
  useDeleteTag,
  useTags,
  useUpdateTag,
  type Tag,
  type TaggableType,
} from '@/lib/api/tags';

const TIPOS: TaggableType[] = ['project', 'event', 'station'];

/**
 * Gestionar el vocabulario de etiquetas de una marca.
 *
 * Etiquetar se hace en las listas —marcando sobre la marcha— y aquí se hace lo
 * otro: repasar lo acumulado, corregir un nombre, cambiar un color y retirar lo
 * que sobra. Son dos tareas distintas y separarlas es lo que evita que el
 * selector de la fila se llene de botones de administración.
 *
 * **Cada etiqueta dice a cuántas entidades alcanza.** Es el dato que decide:
 * retirar una la despega de todo lo que la llevaba, y sin el número esa acción
 * se toma a ciegas. Por eso el recuento viene en el propio listado y no detrás
 * de un clic.
 *
 * Los tres vocabularios van en pestañas separadas porque lo están de verdad: la
 * misma palabra puede existir para proyecto y para estación sin ser la misma
 * etiqueta.
 */
export function TagManagerPage() {
  const t = useT();
  const brandId = useSelectedBrandId();
  const [tipo, setTipo] = useState<TaggableType>('project');

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[
          { label: t('settings.title'), to: '/settings' },
          { label: t('tags.manager.title') },
        ]}
      />

      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          {t('tags.manager.title')}
          <HelpHint section="tags" />
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-fg-muted)]">
          {t('tags.manager.subtitle')}
        </p>
      </header>

      {!brandId ? (
        <EmptyState
          icon={<Tags className="h-6 w-6" />}
          title={t('tags.manager.noBrand')}
          description={t('tags.manager.noBrandBody')}
        />
      ) : (
        <Tabs value={tipo} onValueChange={(v) => setTipo(v as TaggableType)}>
          <TabsList>
            {TIPOS.map((x) => (
              <TabsTrigger key={x} value={x}>
                {t(`tags.type.${x}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {TIPOS.map((x) => (
            <TabsContent key={x} value={x}>
              <ListaDeTipo brandId={brandId} entityType={x} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function ListaDeTipo({
  brandId,
  entityType,
}: {
  brandId: number;
  entityType: TaggableType;
}) {
  const t = useT();
  const q = useTags(brandId, entityType);
  const tags = q.data ?? [];
  const [aBorrar, setABorrar] = useState<Tag | null>(null);

  if (q.isLoading) return <Skeleton className="h-40" />;

  if (tags.length === 0) {
    return (
      <EmptyState
        icon={<Tags className="h-6 w-6" />}
        title={t('tags.manager.empty')}
        description={t('tags.manager.emptyBody', { type: t(`tags.type.${entityType}`) })}
      />
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('tags.manager.count', { n: tags.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-[var(--color-border-subtle)]">
          {tags.map((tag) => (
            <Fila
              key={tag.id}
              tag={tag}
              brandId={brandId}
              entityType={entityType}
              onBorrar={() => setABorrar(tag)}
            />
          ))}
        </CardContent>
      </Card>

      {aBorrar ? (
        <DialogoBorrar
          tag={aBorrar}
          brandId={brandId}
          entityType={entityType}
          onClose={() => setABorrar(null)}
        />
      ) : null}
    </>
  );
}

function Fila({
  tag,
  brandId,
  entityType,
  onBorrar,
}: {
  tag: Tag;
  brandId: number;
  entityType: TaggableType;
  onBorrar: () => void;
}) {
  const t = useT();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(tag.name);
  const actualizar = useUpdateTag(brandId);

  const guardar = () => {
    const limpio = nombre.trim();
    // Sin cambio real no se llama al servidor: un guardado que no guarda nada
    // sigue invalidando la caché y hace parpadear toda la lista.
    if (!limpio || limpio === tag.name) {
      setNombre(tag.name);
      setEditando(false);
      return;
    }
    actualizar.mutate(
      { id: tag.id, name: limpio },
      {
        onSuccess: () => setEditando(false),
        onError: (e: Error) => {
          setNombre(tag.name);
          toast({ tone: 'error', title: t('tags.saveFailed'), description: e.message });
        },
      },
    );
  };

  const usos = tag.usageCount ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-[12rem] flex-1">
        {editando ? (
          <input
            autoFocus
            value={nombre}
            maxLength={40}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') guardar();
              if (e.key === 'Escape') {
                setNombre(tag.name);
                setEditando(false);
              }
            }}
            className="w-full max-w-xs rounded-lg border border-[var(--color-accent-ink)] bg-[var(--color-xk-bg-deep)] px-2.5 py-1.5 text-sm text-[var(--color-fg-primary)] focus:outline-none"
          />
        ) : (
          <TagPill name={tag.name} color={tag.color} />
        )}
      </div>

      {/* Los colores, en línea: cambiar uno es un clic y no merece un diálogo. */}
      <div className="flex items-center gap-1">
        {TAG_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={c}
            title={c}
            onClick={() => actualizar.mutate({ id: tag.id, color: c })}
            className={cn(
              'h-4 w-4 rounded-full border-2 transition-transform hover:scale-110',
              tag.color === c ? 'border-[var(--color-fg-primary)]' : 'border-transparent',
            )}
            style={{ background: tagTint(c) }}
          />
        ))}
      </div>

      {/* El alcance. Es el dato que decide si retirarla o no. */}
      <span className="min-w-[7rem] text-right text-[11px] tabular-nums text-[var(--color-fg-muted)]">
        {usos === 0
          ? t('tags.manager.unused')
          : t(usos === 1 ? 'tags.manager.usedOne' : 'tags.manager.usedMany', { n: usos })}
      </span>

      <div className="flex items-center gap-1">
        {editando ? (
          <>
            <Button size="sm" variant="ghost" onClick={guardar} aria-label={t('common.save')}>
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('stock.dialog.cancel')}
              onClick={() => {
                setNombre(tag.name);
                setEditando(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditando(true)}
            aria-label={t('tags.manager.rename', { name: tag.name })}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onBorrar}
          aria-label={t('tags.manager.remove', { name: tag.name })}
        >
          <Trash2 className="h-4 w-4 text-[var(--color-error,#ef4444)]" />
        </Button>
      </div>
      <span className="sr-only">{entityType}</span>
    </div>
  );
}

/**
 * Confirmar el retirado.
 *
 * Dice cuántas entidades pierden la etiqueta, con el número delante: «se
 * quitará de 12 eventos» es una frase que se lee, y «¿seguro?» no. Cuando no la
 * lleva nadie se dice también, porque entonces la decisión es trivial y no hay
 * que hacerla parecer grave.
 */
function DialogoBorrar({
  tag,
  brandId,
  entityType,
  onClose,
}: {
  tag: Tag;
  brandId: number;
  entityType: TaggableType;
  onClose: () => void;
}) {
  const t = useT();
  const borrar = useDeleteTag(brandId);
  const usos = tag.usageCount ?? 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <div className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-[var(--color-danger-10,rgba(239,68,68,.14))] text-[var(--color-danger-fg,#ef4444)]">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <DialogTitle>{t('tags.manager.removeTitle', { name: tag.name })}</DialogTitle>
          <DialogDescription>
            {usos === 0
              ? t('tags.manager.removeUnused')
              : t(
                  usos === 1
                    ? 'tags.manager.removeBodyOne'
                    : 'tags.manager.removeBodyMany',
                  { n: usos, type: t(`tags.type.${entityType}`).toLowerCase() },
                )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={borrar.isPending}>
            {t('stock.dialog.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={borrar.isPending}
            onClick={() =>
              borrar.mutate(tag.id, {
                onSuccess: () => {
                  toast({ tone: 'success', title: t('tags.manager.removed', { name: tag.name }) });
                  onClose();
                },
                onError: (e: Error) =>
                  toast({ tone: 'error', title: t('tags.saveFailed'), description: e.message }),
              })
            }
          >
            {borrar.isPending ? t('brandDelete.deleting') : t('tags.manager.removeConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
